import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Autocomplete, TextField } from "common";
import { useEffect, useMemo, useState } from "react";

// Generic props for the filter
interface AutoCompleteFilterProps<T> {
  filterKey: string;
  filterKey2?: string; // Add second filter key for keyword
  filter?: Record<string, any>;
  onChange: (filter: Record<string, any>) => void;
  options: T[];
  title?: string;
  label?: string;
  placeholder?: string;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => number | string;
  multiple?: boolean;
  keywordLabel?: string; // Optional label for keyword field
  keywordPlaceholder?: string;
  keywordType?: string;
}

function AutoCompleteFilter<T>({
  filterKey,
  filterKey2,
  filter,
  onChange,
  options,
  title,
  label,
  placeholder,
  getOptionLabel,
  getOptionValue,
  multiple = true,
  keywordLabel = "Kulcsszó", // default label
  keywordPlaceholder = "Keresés...",
  keywordType = "text",
}: AutoCompleteFilterProps<T>) {
  // Always store selected as array of IDs (number[] or string[])
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<Array<number | string>>(
    filter?.[filterKey] || []
  );
  const [keyword, setKeyword] = useState<string | null>(
    filter?.[filterKey2 || ""] || null
  );

  const mappedOptions = useMemo(
    () =>
      options.map(opt => ({
        value: getOptionValue(opt),
        label: getOptionLabel(opt),
        original: opt,
      })),
    [options, getOptionLabel, getOptionValue]
  );

  useEffect(() => {
    if (filter?.[filterKey]) {
      setSelected(filter[filterKey]);
    }
    if (filterKey2 && filter?.[filterKey2]) {
      setKeyword(filter[filterKey2]);
    }
  }, [filter, filterKey, filterKey2]);

  const handleOpen = (evt: any) => {
    evt.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    const newFilter: Record<string, any> = { [filterKey]: selected };
    if (filterKey2)
      newFilter[filterKey2] =
        keyword && keyword?.trim() !== "" ? keyword : undefined;
    if (
      JSON.stringify(filter?.[filterKey]) !== JSON.stringify(selected) ||
      (filterKey2 && filter?.[filterKey2] !== keyword)
    ) {
      if (newFilter) onChange(newFilter);
    }
  };
  const hasFilter =
    selected?.length > 0 || (filterKey2 && keyword && keyword?.trim() !== "");

  return (
    <>
      <IconButton
        color={hasFilter ? "info" : undefined}
        size="small"
        onClick={handleOpen}
        sx={{ position: "absolute", right: 0, top: 10 }}
      >
        {hasFilter ? <FilterAltIcon /> : <FilterAltOutlinedIcon />}
      </IconButton>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Autocomplete
            label={label}
            placeholder={placeholder}
            options={mappedOptions}
            multiple={multiple}
            value={selected}
            onChange={(evt: any) => setSelected(evt.target.value)}
            color="secondary"
          />
          {filterKey2 && (
            <TextField
              clearable
              label={keywordLabel}
              placeholder={keywordPlaceholder}
              type={keywordType}
              value={keyword}
              onChange={(e: any) => setKeyword(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleClose();
                }
              }}
              fullWidth
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Ok</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AutoCompleteFilter;
